import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  getFanScanErrorMessage,
  getFanScanPayload,
  getFanScanResultCategory,
  isFanScanApiSuccess,
  isFanScanAuthError,
  isFanScanConflict,
  listFanScanResultCategories,
  updateFanScanResultCategory,
} from '../../services/fanScanApi';
import {
  RESULT_DESCRIPTION_MAX,
  RESULT_TITLE_MAX,
  allowsHttpImageUrls,
  buildResultCategorySaveBody,
  bundleIsEditable,
  collectResultCategoryErrors,
  formatUtcStamp,
  identityTitle,
  imageUrlError,
  resultCategoriesFromPayload,
  resultCategoryFromPayload,
  toResultCategoryDraft,
} from './fanScanUtils';

function ImageUrlField({ value, disabled, allowHttp, onChange }) {
  const problem = imageUrlError(value, { allowHttp, subject: 'Image' });
  const preview = String(value || '').trim();
  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Image"
        value={value}
        disabled={disabled}
        error={Boolean(problem)}
        helperText={
          problem ||
          (allowHttp
            ? 'Public https URL, or http on this API. Clear the field to remove the image.'
            : 'Public https URL. Clear the field to remove the image.')
        }
        onChange={(event) => onChange(event.target.value)}
      />
      {preview && !problem ? (
        <Box
          component="img"
          src={preview}
          alt=""
          sx={{ mt: 1, maxHeight: 160, maxWidth: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : null}
    </Box>
  );
}

function FanScanResults({ selectedId, onSelect, bundleStatus, iconsById }) {
  const [loadingList, setLoadingList] = useState(true);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [draft, setDraft] = useState(null);

  const editable = bundleIsEditable(bundleStatus);
  const allowHttp = allowsHttpImageUrls();

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    listFanScanResultCategories().then((res) => {
      if (cancelled) return;
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setError(getFanScanErrorMessage(res, 'Failed to load result identities.'));
        setLoadingList(false);
        return;
      }
      setCategories(resultCategoriesFromPayload(getFanScanPayload(res)));
      setError('');
      setLoadingList(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setCategory(null);
      setDraft(null);
      setFormError('');
      return undefined;
    }
    let cancelled = false;
    setLoadingCategory(true);
    setFormError('');
    setNotice('');
    getFanScanResultCategory(selectedId).then((res) => {
      if (cancelled) return;
      setLoadingCategory(false);
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setCategory(null);
        setDraft(null);
        setFormError(
          res?.httpStatus === 404
            ? 'That identity was not found.'
            : getFanScanErrorMessage(res, 'Failed to load this identity.')
        );
        return;
      }
      const next = resultCategoryFromPayload(getFanScanPayload(res));
      setCategory(next);
      setDraft(toResultCategoryDraft(next));
      setFormError(next ? '' : 'That identity was not found.');
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const save = async () => {
    const errors = collectResultCategoryErrors(draft, { allowHttp });
    if (errors.length) {
      setFormError(errors.join(' '));
      setNotice('');
      return;
    }
    const body = buildResultCategorySaveBody(category, draft);
    if (!body) {
      setFormError('');
      setNotice('Nothing changed.');
      return;
    }
    setSaving(true);
    setFormError('');
    setNotice('');
    const res = await updateFanScanResultCategory(category.id, body);
    setSaving(false);
    if (isFanScanAuthError(res)) {
      window.location = '/login';
      return;
    }
    if (!isFanScanApiSuccess(res)) {
      setFormError(
        isFanScanConflict(res)
          ? getFanScanErrorMessage(res, 'Approved bundles cannot be edited.')
          : getFanScanErrorMessage(res, 'Could not save this identity.')
      );
      return;
    }
    const saved = resultCategoryFromPayload(getFanScanPayload(res)) || { ...category, ...body };
    setCategory(saved);
    setDraft(toResultCategoryDraft(saved));
    setCategories((current) => current.map((item) => (item.id === saved.id ? { ...item, ...saved } : item)));
    setNotice(
      'Saved. New completions use this title and description. Finished results keep the title and description they were given. The image updates on every result, including ones that already finished.'
    );
  };

  const titleLength = String(draft?.title ?? '').trim().length;
  const descriptionLength = String(draft?.description ?? '').trim().length;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 1, pt: 1 }}>
            These 10 identities are fixed. A new completion uses the saved title and description. A finished result keeps that title and description. The image is read live, so new art shows on results that already finished.
          </Typography>
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense aria-label="Fan Scan result identities">
              {categories.map((item) => (
                <ListItemButton
                  key={item.id}
                  selected={item.id === selectedId}
                  onClick={() => onSelect(item.id)}
                >
                  <ListItemText
                    primary={`${iconsById?.[item.id] ? `${iconsById[item.id]} ` : ''}${identityTitle(item)}`}
                    secondary={item.id}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {error ? (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          ) : null}
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        {!selectedId ? (
          <Paper sx={{ p: 3 }}>
            <Typography>Choose an identity to edit the title, description, and image players see.</Typography>
          </Paper>
        ) : loadingCategory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Paper sx={{ p: 2 }}>
            {!editable ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This bundle is {bundleStatus}, so identities cannot be edited. Only a draft can be saved.
              </Alert>
            ) : null}
            {formError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            ) : null}
            {notice ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {notice}
              </Alert>
            ) : null}

            {category && draft ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="h6">
                    {iconsById?.[category.id] ? `${iconsById[category.id]} ` : ''}
                    {identityTitle(category)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.id}. The emoji stays as it is. Description is plain text, not markdown. Title and description are stored when a scan finishes. The image is the live one.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {category.updated_at
                      ? `Last save ${formatUtcStamp(category.updated_at)}`
                      : 'This identity has not been saved in admin yet.'}
                  </Typography>
                </Box>

                <TextField
                  label="Title"
                  value={draft.title}
                  disabled={!editable}
                  error={titleLength > RESULT_TITLE_MAX}
                  helperText={`${titleLength} / ${RESULT_TITLE_MAX}`}
                  onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                />
                <TextField
                  label="Description"
                  value={draft.description}
                  disabled={!editable}
                  multiline
                  minRows={4}
                  error={descriptionLength > RESULT_DESCRIPTION_MAX}
                  helperText={`${descriptionLength} / ${RESULT_DESCRIPTION_MAX}`}
                  onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                />
                <ImageUrlField
                  value={draft.image_url}
                  disabled={!editable}
                  allowHttp={allowHttp}
                  onChange={(image_url) => setDraft((current) => ({ ...current, image_url }))}
                />
                <Box>
                  <Button variant="contained" onClick={save} disabled={!editable || saving}>
                    {saving ? 'Saving…' : 'Save identity'}
                  </Button>
                </Box>
              </Box>
            ) : null}
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}

export default FanScanResults;
